// External dependencies
import moment from 'moment';
import cloneDeep from 'lodash.clonedeep';
import merge from 'lodash.merge';

// Internal dependencies
import { formatAREAScore, sortActivitiesByDate } from '../../../../../services/util';
import getActivityType from '../../../util/getActivityType';
import redx from 'Assets/reports/redx.png';
import { getTotalSeconds } from '../../../util/getTotalSeconds';
import { isEarlyReader, shouldFilterStoryFromReport } from '../../../../../services/EarlyReaderService';
import { scoreFormat } from '../../../util/numberFormatters/scoreFormat';
import { categoryLabel, getCutscoreColors, isUsingFourCategories } from '../../../util/cutscores';

// Formats activities for plotting
function formatProgressReportActivities(reportData) {
  let reformattedData = [];
  for(let i = 0; i < reportData.data.length; i++){
    let dataValue = reportData.data[i];
    //don't plot if null value or if activity is early reader type that we should filter
    if(!dataValue || shouldFilterStoryFromReport(dataValue, reportData.scale)){
      reformattedData.push(null);
    } else {
      let xValue = dataValue.date;
      let yValue = dataValue.score;
      let activityType = getActivityType(dataValue.type, dataValue.storyTags,dataValue.date);
      let activityId = dataValue.id;
      let storyTags = dataValue.storyTags;
      let timeRead = dataValue.timeRead;

      // TODO potentially use this code in the future,
      // if we want to change point state based on cutoff
      /* let poorCutoff = getCutoffForDate(xValue, reportData.poorCutoff); //TODO swap in functions from the backend for these
      let averageCutoff = getCutoffForDate(xValue, reportData.averageCutoff) + poorCutoff;
      let markerColor = reportColors.POOR;
      if(yValue > poorCutoff){
        markerColor = reportColors.AVERAGE;
      }
      if(yValue > averageCutoff){
        markerColor = reportColors.GREAT;
      }*/

      let wrappedValue = {y: yValue, x: xValue, activityType, activityId, storyTags, timeRead};
      if(yValue < 0){ // negative values get a different color
        wrappedValue.marker = {fillColor: "#29292B"};
      }

      //TODO decide whether we want to do this with the data format
      /*if(i == 0){
        wrappedValue.x = 0.15;  //bump the first data point over a bit so that it doesn't run off the edge
      }*/
      reformattedData.push(wrappedValue);

      if(i == 0){
        reformattedData.push(null); //TODO figure out why this hack is necessary
      }
    }
  }
  return reformattedData;
}

function formatProgressReportData(reportData, showBenchmarks){
  // Format the activities for plotting
  let reformattedData = formatProgressReportActivities(reportData);

  // calculate slope and determine marker at the end
  const x = reportData.trendline.map((item) => item[0])
  const y = reportData.trendline.map((item) => item[1])

  const maxX = Math.max(...x)
  const maxY = Math.max(...y)

  // normalize values
  const rise = (y[y.length - 1] - y[0]) / maxY;
  const run = (x[x.length - 1] - x[0]) / maxX;

  // calculate the slope
  const slope = rise / run;

  const marker = {
    symbol: "circle",
    radius: 9,
    fillColor: "yellow"
  };

  if (slope > 0.1) {
    marker.symbol = "triangle";
    marker.fillColor = "green"
  }

  if (slope < -0.2) {
    marker.symbol = "triangle-down";
    marker.fillColor = "red"
  }

  // Format the trendline
  let reformattedTrendline = [];
  let xval;
  if (showBenchmarks) {
    //add points on the trendline where there are 1+ early reader activities
    for (let i = 0; i < reportData.data.length; i++) {
      let point = reportData.data[i];
      if (shouldFilterStoryFromReport(point, reportData.scale)) {
        xval = point.date;
        let trendline = reportData.trendline;
        for (let j = 1; j < trendline.length - 1; j++) {
          if (xval < trendline[j][0]) {
            let trendPoint = {
              activityId: point.id,
              y: (trendline[j][1] + trendline[j - 1][1])/2,
              x: (trendline[j - 1][0] + trendline[j][0])/2,
              marker: {
                symbol: "circle",
                radius: 5,
                fillColor: "#4A5CFF"
              },
            }
            if (!reformattedData.includes(trendPoint))
            reformattedData.push(trendPoint)
            break;
          }
        }
      }
    }
  }

  //draw the trendline
  for(let i = 0; i < reportData.trendline.length; i++) {
    let dataValue = reportData.trendline[i];
    if(!dataValue) {
      continue;
    }
    reformattedTrendline.push({
      y: reportData.trendline[i][1],
      x: reportData.trendline[i][0],
    });

    //When the trendline is visible (using adjusted scores, not raw scores),
    //add a triangle for the terminal point of the trendline to the scatter series.
    if(showBenchmarks && (i == reportData.trendline.length - 1)){
      reformattedData.push({
        y: reportData.trendline[i][1],
        x: reportData.trendline[i][0],
        marker
      });
    }
  }
  reportData = Object.assign({}, reportData, {data: reformattedData});

  return Object.assign({}, reportData, {trendline: reformattedTrendline});
}

// this is a temporary solution which will not be 100% accurate. This will be replaced with a function from the backend
function getCutoffForDate(date, cutoffs){
  for(let i = 0; i < cutoffs.length; i++){
    let cutoff = cutoffs[i];
    if(cutoff[0] > date){
      return cutoff[1];
    }
  }
}

function rescaleIfNegativeScoresExist(reportData) {
  let hasNegativeScores = false;

  // if negative scores exist, rescale
  if(reportData.yMin < 0) {
    hasNegativeScores = true;

    // Rescale data
    reportData.data = reportData.data.map(point => {
      point.score = point.score - reportData.yMin;
      return point;
    });

    // Rescale benchmarks
    reportData.poorCutoff = reportData.poorCutoff.map(point => {
      point[1] = point[1] - reportData.yMin;
      return point;
    });

    reportData.yMax = reportData.yMax - reportData.yMin;

    // Rescale trendline
    reportData.trendline = reportData.trendline.map(trendPoint => {
      trendPoint[1] = trendPoint[1] - reportData.yMin;
      return trendPoint;
    });
  }

  let scaledReportData = reportData;
  return {scaledReportData, hasNegativeScores};
}

export function getProgressReportConfig(data, onClick, embedded, legendIsOnBottom, legendText, isDistrictBenchmark, selectedEquating, selectedStatePercentileCuts, t){
  if(!data){
    return {};
  }
  let reportData = cloneDeep(data);

  let {scaledReportData, hasNegativeScores} = rescaleIfNegativeScoresExist(reportData);
  if(hasNegativeScores) {
    reportData = scaledReportData;
  }

  reportData = formatProgressReportData(reportData, selectedEquating);

  // if we are using raw scores or don't have cutoffs, use a dark grey point color
  let pointColor = "#ffffff"; // TODO refactor to handle a variable number of cutlines
  if(!selectedEquating || reportData.poorCutoff.length == 0 || reportData.averageCutoff.length == 0 || reportData.goodCutoff.length == 0) {
    pointColor = "#29292B";
  }

  let yAxismin = reportData.yMin;
  if(reportData.yMin < 0) {
    yAxismin = 0;
  }

  let chartHeight = 344;
  if(embedded) {
    chartHeight = "35%";
    if(legendIsOnBottom) {
      chartHeight = "50%";
    }
  }

  let config = {
    chart:{
      height: chartHeight,
      styledMode: true,
    },
    title:{
      text: null,
    },
    credits: {
      enabled: false
    },
    plotOptions: {
      series: {
        connectNulls: true,
      },
      scatter: {
        cursor: 'pointer',
        lineWidth: 0,
        color: pointColor,
        /*events: {
          afterAnimate() {
            if(Array.isArray(this.points) && this.points.length > 1){//We need at least an array with 2 elements
              const mostRecentActivity = sortActivitiesByDate(this.points)[this.points.length - 2];
              this.chart.tooltip.refresh(mostRecentActivity);
            }
          }
        },*/ //TODO: re-enable this when tooltip is fixed for students with very few activities https://amiralearning.atlassian.net/browse/AE-3062
        marker: {
          symbol: "circle",
          radius: 6,
          enabled: true,
          fillColor: pointColor,
          lineWidth: 1,
          lineColor: 'black',
        },
        point: {
          events: {
              click: function(e) {
                onClick(e); // redirects to activity page
              }
          }
        }
      },
      area: {
        stacking: 'normal',
        lineWidth: 0,
        marker: {
          enabled: false,
        },
        events: {
          legendItemClick: function () {
            return false;
          },
        },
      },
    },
    tooltip: {
      formatter: function () {
        let type = t('Predicted Ability');
        if(this.point.activityType) {
          if(this.point.activityType == 'Tutor') {
            type = t('Practice');
            if (isEarlyReader(this.point)) {
              type = t('Practice (Early Reader)')
            }
          } else {
            type = t(this.point.activityType);
          }
        }
        let roughScore = this.y;
        let unit = t(this.series.yAxis.axisTitle.textStr);
        if(reportData.yMin < 0) {
          roughScore = roughScore + reportData.yMin;
          // TODO this is hacky, and won't extend to other negative scales
          if(reportData.scale !== 'ARM'){
            if(roughScore < 0) {
              unit = 'BR';
            }
            roughScore = Math.abs(roughScore);
          }
        }
        let score;
        if(reportData.scale == 'AREA') { // TODO use enum
          score = parseFloat(parseFloat(roughScore).toFixed(1));
        } else if(reportData.scale === 'ARM') {
          score = parseFloat(parseFloat(roughScore).toFixed(2));
          const today = moment().startOf('day');
          const tooltipDate = moment(this.x).startOf('day');
          if (tooltipDate.isSameOrBefore(today)) {
            type = t('Estimated Mastery');
          }
        }else {
          score = Math.round(roughScore * 10) / 10;
        }

        moment.locale(reportData.locale)
        const date = moment(this.x).format("L")

        return '<b><span style="font-size: 12px; font-weight: bold;">' + type + "</span></b><br>" + '<span style="font-size: 20px;">' + scoreFormat(score) + ' ' + unit +
        "</span><br><span style='font-size: 14px'>" + date +
          "</span>";
      },
      backgroundColor:"#ffffff",
      padding: 0,
      className: "progressTooltip",
      useHTML: true,
      style: {
        cursor: "pointer",
        pointerEvents: "auto",
      },
    },
    legend: {
      enabled: embedded,
      symbolRadius: 0,
      symbolWidth: embedded ? 24 : 20,
      symbolHeight: embedded ? 24 : 20,
      align: legendIsOnBottom ? "center" : "right",
      verticalAlign: legendIsOnBottom ? 'bottom' : 'top',
      layout: legendIsOnBottom ? 'horizontal':'vertical',
      title: {
            text: '',
            style: {
              "font-size": "14px",
            }
        },
      itemMarginTop: legendIsOnBottom ? 16: 8,
      itemStyle: {
        'cursor': 'default',
        "fontWeight": "default",
        "fontSize": "16px",
        "color":"#aaaaaa",
      },
    },
    xAxis: {
      min: reportData.yearStart,
      max: reportData.yearEnd,
      tickLength: 0,
      type: 'datetime',
      dateTimeLabelFormats:{
        day: '%b'
      },
      labels: {
        y: 30,
        style: {
          "font-weight": "bold",
          "font-size": "16px",
        },
      },
    },
    yAxis: {
      max: reportData.yMax,
      min: yAxismin,
      title: {
        text: reportData && reportData.label,
        style: {
          "font-size": "16px",
        }
      },
      labels: {
        style: {
          "font-weight": "bold",
          "font-size": "16px",
        },
        formatter: function () {
          var label = this.axis.defaultLabelFormatter.call(this);
          let parsedLabel = parseFloat(label);
          let yMin = reportData.yMin;
          if(reportData.scale == "Lexile"){   //TODO: HACK remove
            parsedLabel = Math.round(parsedLabel);
            yMin = Math.round(yMin);
          }

          if(reportData.yMin < 0){
            if((parseFloat(label) + yMin < 0) && (reportData.scale !== 'ARM')) {
              label = Math.abs(parsedLabel + yMin) + " BR";
            } else {
              label = parsedLabel + yMin;
              if(reportData.scale == 'AREA'){ // TODO use enum
                label = parseFloat(parseFloat(label).toFixed(1));
              } else if(reportData.scale === 'ARM') {
                label = parseFloat(parseFloat(label).toFixed(2));
              }
            }
          }

          if(reportData.scale == 'AREA') { // TODO use enum
            label = formatAREAScore(parsedLabel, false, true);
          }
          return label;
        }
      },
      maxPadding: 0,
      startOnTick: false,
      endOnTick: false,
    },
  };

  if(selectedEquating) { // TODO don't generate trendline if # of points is too low
    config.plotOptions.line = {
      lineWidth: 4,
      lineColor: "#4A5CFF",
      dashStyle: 'LongDash',
    };
  } else {
    //draw styled container to provide visual boundry
    //since benchmark areas are hidden.
    config.chart = merge(config.chart, {
      spacing: [15, 15, 15, 15],
      borderColor: '#cccccc',
      borderWidth: 1,
      borderRadius: 20,
      backgroundColor: '#f9f6f2',
    });
  }

  var reportDataWithSymbols = reportData && cloneDeep(reportData.data);
  reportDataWithSymbols.map((point) => {
    if (point && !point.marker) {
      const { timeRead } = point;
      const totalSeconds = getTotalSeconds(timeRead);
      point.marker = getMarker(point, totalSeconds, reportData.scale);
    }
  });

  const studentPerformanceSeries = {
    name: (reportData && reportData.studentName) + "'s performance",
    showInLegend: false,
    type: 'scatter',
    data: reportDataWithSymbols,
    color: '#ffffff',
  };

  const excellent = t("common:ahead")
  const average = t("common:on_track")
  const belowExpectation = t("common:catch_up")

  const colors = getCutscoreColors(selectedStatePercentileCuts)
  const isFourCutlines = isUsingFourCategories(selectedStatePercentileCuts);
  const labels = categoryLabel;

  const cutlineSeries = isFourCutlines ?
    [{
      name: t(labels.GREAT),
      type: "area",
      data: reportData?.greatCutoff,
      color: colors.GREAT,
      enableMouseTracking: false,
    }, {
      name: t(labels.GOOD),
      type: "area",
      data: reportData?.goodCutoff,
      color: colors.GOOD,
      enableMouseTracking: false,
    }, {
      name: t(labels.AVERAGE),
      type: "area",
      data: reportData?.averageCutoff,
      color: colors.AVERAGE,
      enableMouseTracking: false,
    }, {
      name: t(labels.POOR),
      type: "area",
      data: reportData?.poorCutoff,
      color: colors.POOR,
      enableMouseTracking: false,
    }] :
    [{
      name: isDistrictBenchmark ? ('Above ' + legendText.green) : excellent,
      type: "area",
      data: reportData?.greatCutoff,
      color: colors.GREAT,
      enableMouseTracking: false,
    },
    {
      name: isDistrictBenchmark ? legendText.yellow : average,
      type: "area",
      data: reportData?.onTrackCutoff,
      color: colors.AVERAGE,
      enableMouseTracking: false,
    },
    {
      name: isDistrictBenchmark ? ('Below ' + legendText.red) : belowExpectation,
      type: "area",
      data: reportData?.poorCutoff,
      color: colors.POOR,
      enableMouseTracking: false,
    }];

  config.series = selectedEquating ?
    [
      ...cutlineSeries,
      studentPerformanceSeries,
      {
        name: (reportData && reportData.studentName) + "'s trendline",
        showInLegend: false,
        type: 'line',
        data: reportData && reportData.trendline,
        color: '#ffffff',
      },
    ] : [studentPerformanceSeries];

  return config;
}

function getMarker(activity, s, scale) {
  if (s < 30) return {symbol: `url(${redx})`, lineColor: "black", lineWidth: 1, radius: 6};

  switch (activity.activityType) {
    case "Tutor":
    if (shouldFilterStoryFromReport(activity, scale)) return null;
      return {symbol: 'triangle-down', fillColor: 'white', radius: 6};
    case "Assessment":
      return {symbol: 'diamond', fillColor: '#7FDBFF', radius: 6};
    case "Progress Monitor":
      return {symbol: 'square', fillColor: '#F012BE', radius: 6};
  }
}
